"use client";

import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader as Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
export default function TechnicalInspectionCTA() {
  return <section id="technicalinspectioncta" className="relative bg-background py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
      backgroundImage: `linear-gradient(to right, var(--accent) 1px, transparent 1px), linear-gradient(to bottom, var(--accent) 1px, transparent 1px)`,
      backgroundSize: "40px 40px"
    }} aria-hidden="true" />

      <div className="container mx-auto px-6 relative z-10 max-w-4xl">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary font-default mb-6 tracking-tight">
            Vuoi sapere cosa c’è davvero dentro i tubi della tua cappa?
          </h2>
          <p className="text-lg md:text-xl text-secondary max-w-3xl mx-auto font-default">
            Aera esegue sopralluoghi tecnici su cappe professionali e tubazioni
            di estrazione cucina, con ispezione, intervento interno e
            documentazione finale.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2,
        ease: "easeOut"
      }} className="bg-card p-8 md:p-12 shadow-xl border border-border/50 relative">
          {}
          <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-accent/50" />
          <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-accent/50" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-accent/50" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-accent/50" />

          <div className="mb-8 border-b border-border pb-4">
            <h3 className="text-xl font-semibold text-primary font-default uppercase tracking-wider flex items-center gap-2">
              <span className="w-2 h-2 bg-accent inline-block" />
              Richiedi Intervento Specialistico
            </h3>
          </div>

          <FormProvider formId="intervention_form_technical_inspection" sectionName="technical-inspection-cta" defaultValues={{
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: false
          },
          subject: {
            required: false
          },
          message: {
            required: true,
            minLength: 10
          }
        }} submitText="Richiedi sopralluogo tecnico" successMessage="Richiesta di sopralluogo inviata con successo. Il nostro reparto tecnico ti contatterà a breve per pianificare l'intervento.">
            <InspectionForm />
          </FormProvider>
        </motion.div>
      </div>
    </section>;
}
function InspectionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="p-8 bg-accent/10 border border-accent/20 text-center">
        <div className="w-16 h-16 bg-accent/20 rounded-lg flex items-center justify-center mx-auto mb-4">
          <ArrowRight className="w-8 h-8 text-accent" />
        </div>
        <h4 className="text-xl font-bold text-primary font-default mb-2">
          Operazione Completata
        </h4>
        <p className="text-secondary mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-accent text-accent hover:bg-accent hover:text-accent-foreground rounded-sm">
          Invia una nuova richiesta
        </Button>
      </motion.div>;
  }
  const schematicInputClasses = "border-0 border-b-2 border-border rounded-sm bg-transparent px-0 py-2 shadow-sm focus-visible:ring-0 focus-visible:border-accent transition-colors text-foreground placeholder:text-muted-foreground/70";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Il nome o la ragione sociale è obbligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-bold text-secondary uppercase tracking-wider">
                NOME E COGNOME / RAGIONE SOCIALE *
              </FormLabel>
              <FormControl>
                <Input placeholder="Mario Rossi" className={schematicInputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "L'email aziendale è obbligatoria",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Inserisci un indirizzo email valido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-bold text-secondary uppercase tracking-wider">
                EMAIL AZIENDALE *
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="info@azienda.it" className={schematicInputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-bold text-secondary uppercase tracking-wider">
                TELEFONO
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+39 000 000 0000" className={schematicInputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-bold text-secondary uppercase tracking-wider">
                TIPO DI IMPIANTO
              </FormLabel>
              <FormControl>
                <select className={`w-full h-9 outline-none ${schematicInputClasses}`} {...field}>
                  <option value="" disabled>
                    Seleziona tipologia
                  </option>
                  <option value="cappe_cucina">Cappe Cucina Professionali</option>
                  <option value="condotti_hvac">Condotti Aria HVAC</option>
                  <option value="sistemi_aspirazione">Sistemi di Aspirazione Industriale</option>
                  <option value="altro">Altro</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "La descrizione dell'esigenza è obbligatoria"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-bold text-secondary uppercase tracking-wider">
              DESCRIZIONE ESIGENZA *
            </FormLabel>
            <FormControl>
              <textarea placeholder="Descrivi brevemente la necessità tecnica..." className={`w-full min-h-[120px] resize-y outline-none ${schematicInputClasses}`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <div className="pt-4 flex justify-end">
        <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-8 text-lg font-semibold rounded-sm shadow-md w-full md:w-auto group">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 size-5" />
              Elaborazione in corso...
            </> : <>
              Richiedi sopralluogo tecnico
              <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
            </>}
        </Button>
      </div>
    </Form>;
}